import { useEffect, useMemo, useRef } from 'react'

import { debounce } from 'lodash'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { useMutateSavePOPDFLabelOverrides } from '@webapp/modules/purchasing/features/save-po-pdf-label-overrides/models'
import {
  type PDFLabelFieldMetadata,
  type PDFLabelSet,
} from '@webapp/modules/vendor/entities/PDFLabelSet' // eslint-disable-line import/no-internal-modules
import messages from './messages'

const UPDATE_TOAST_DEBOUNCE_MS = 2000

/**
 * Whole-translation PUT per override commit (BHV-040). The success toast is
 * debounced 2s so rapid consecutive commits produce a single toast — parity
 * with the Angular `showUpdateNotification` debounce.
 */
export const useSavePOPDFLabelOverridesPresenter = () => {
  const intl = useProcurifyIntl()
  const { mutateAsync, isLoading } = useMutateSavePOPDFLabelOverrides()

  const intlRef = useRef(intl)
  intlRef.current = intl

  // Stable for the hook's lifetime (empty deps) so rapid commits share one
  // trailing invocation; intl is read through a ref
  const showUpdateToast = useMemo(
    () =>
      debounce((name: string) => {
        toast.success({
          title: intlRef.current.formatMessage(messages.successTitle),
          children: intlRef.current.formatMessage(messages.successMessage, {
            name,
          }),
        })
      }, UPDATE_TOAST_DEBOUNCE_MS),
    []
  )

  const isMountedRef = useRef(true)

  useEffect(
    () => () => {
      isMountedRef.current = false
      showUpdateToast.cancel()
    },
    [showUpdateToast]
  )

  const saveOverride = (
    set: PDFLabelSet,
    translationType: PDFLabelFieldMetadata['translationType'],
    fieldKey: string,
    value: string
  ) => {
    mutateAsync({ labelSet: set, translationType, fieldKey, value })
      .then(() => {
        // Guard against scheduling a toast for an unmounted consumer
        if (isMountedRef.current) {
          showUpdateToast(set.value.name)
        }
      })
      .catch((error) => {
        const errorMessage =
          getMessageFromError(error) ||
          intl.formatMessage(messages.defaultErrorMessage)

        toast.error({
          title: intl.formatMessage(messages.errorTitle),
          children: errorMessage,
        })
      })
  }

  return {
    commands: {
      saveOverride,
    },
    states: {
      isSaving: isLoading,
    },
  }
}
